import { isEmpty } from 'ramda';
import type { RowFilterCondition, RowFilterConditionOrNil, Condition, OrConditions } from '@qre-mailing/smart-table';

/**
 * Checks if a filter is empty (e.g. "All" filter)
 */
export function isEmptyFilter(filter: RowFilterConditionOrNil): boolean {
  const andConditions = filter?.andConditions;
  if (!andConditions || isEmpty(andConditions)) {
    return true;
  }
  return andConditions.every((ac) => isEmpty(ac?.orConditions));
}

/**
 * Compares two ConditionDto for equality (field, operator, value)
 */
function conditionsEqual(a: Condition, b: Condition): boolean {
  if (a.field !== b.field || a.operator !== b.operator) return false;
  const aVal = a.value === undefined ? null : a.value;
  const bVal = b.value === undefined ? null : b.value;
  return JSON.stringify(aVal) === JSON.stringify(bVal);
}

/**
 * Checks if a condition exists in the list (by reference or deep equality)
 */
function conditionExistsInList(condition: Condition, list: Condition[]): boolean {
  return list.some((c) => conditionsEqual(c, condition));
}

/**
 * Checks if two OrConditionsDto are equal (same conditions)
 */
function orConditionsEqual(a: OrConditions, b: OrConditions): boolean {
  const aList = a.orConditions ?? [];
  const bList = b.orConditions ?? [];
  if (aList.length !== bList.length) return false;
  return aList.every((ac) => conditionExistsInList(ac, bList));
}

/**
 * Checks if andCondition exists in the list
 */
function andConditionExistsInList(ac: OrConditions, list: OrConditions[]): boolean {
  return list.some((c) => orConditionsEqual(ac, c));
}

/**
 * Checks if the predefined filter is part of the current condition (all filter andConditions exist in condition)
 */
export function isPredefinedFilterActive(condition: RowFilterConditionOrNil, filter: RowFilterCondition): boolean {
  if (isEmptyFilter(filter)) {
    return isEmptyFilter(condition);
  }
  if (!condition?.andConditions?.length) return false;

  const filterAndConds = filter.andConditions ?? [];
  const conditionAndConds = condition.andConditions;

  return filterAndConds.every((fac) => andConditionExistsInList(fac, conditionAndConds));
}

/**
 * Merges the filter's conditions into the current condition (AND logic).
 * Skips merge if filter is empty (e.g. "All").
 */
export function mergePredefinedFilters(
  condition: RowFilterConditionOrNil,
  filter: RowFilterCondition
): RowFilterCondition {
  if (isEmptyFilter(filter)) {
    return condition ?? { andConditions: [], maxRecords: undefined };
  }

  const existingAndConds = condition?.andConditions ?? [];
  const filterAndConds = filter.andConditions ?? [];
  const mergedAndConds = [...existingAndConds];

  for (const fac of filterAndConds) {
    if (!andConditionExistsInList(fac, mergedAndConds)) {
      mergedAndConds.push({ ...fac });
    }
  }

  const maxRecords = condition?.maxRecords ?? filter.maxRecords;

  return {
    andConditions: mergedAndConds,
    maxRecords: maxRecords ?? undefined,
  };
}

/**
 * Removes the filter's andConditions from the condition.
 * Returns null if no conditions remain.
 */
export function removePredefinedFilterFromCondition(
  condition: RowFilterCondition,
  filter: RowFilterCondition
): RowFilterConditionOrNil {
  if (isEmptyFilter(filter)) return condition;

  const filterAndConds = filter.andConditions ?? [];
  const remaining = condition.andConditions.filter((ac) => !filterAndConds.some((fac) => orConditionsEqual(ac, fac)));

  if (remaining.length === 0) return null;

  return {
    andConditions: remaining,
    maxRecords: condition.maxRecords ?? undefined,
  };
}
